import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { config } from '../config/env';
import { Avatar, AvatarFallback } from '../components/ui/Avatar';
import { Icon } from '../components/ui/Icon';
import styles from './ActivateAccount.module.css';

const ActivateAccount = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [activationStatus, setActivationStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [activationResult, setActivationResult] = useState(null);

  useEffect(() => {
    if (token) {
      fetchTokenInfo();
    } else {
      setActivationStatus('error');
      setErrorMessage('Invalid activation link. No token provided.');
    }
  }, [token]);

  const fetchTokenInfo = async () => {
    try {
      setLoading(true);
      const apiUrl = window.location.origin;
      const response = await fetch(`${apiUrl}/api/v1/activation/token/${token}`);

      if (!response.ok) {
        if (response.status === 404) {
          setActivationStatus('error');
          setErrorMessage('Invalid or expired activation token.');
        } else {
          setActivationStatus('error');
          setErrorMessage('Failed to verify activation token.');
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      setTokenInfo(data);

      if (data.used) {
        setActivationStatus('already_used');
      } else if (data.expired) {
        setActivationStatus('expired');
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching token info:', error);
      setActivationStatus('error');
      setErrorMessage('Failed to connect to server. Please try again.');
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      setLoading(true);
      const apiUrl = window.location.origin;
      const response = await fetch(`${apiUrl}/api/v1/activation/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        setActivationStatus('error');
        setErrorMessage(data.detail || 'Activation failed. Please contact support.');
        setLoading(false);
        return;
      }

      setActivationResult(data);
      setActivationStatus('success');
      setLoading(false);
    } catch (error) {
      console.error('Error activating account:', error);
      setActivationStatus('error');
      setErrorMessage('Failed to activate account. Please try again.');
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    window.location.href = config.COMMON_AUTH_LOGIN_URL;
  };

  if (loading && !tokenInfo) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Verifying activation token...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.logoSection}>
            <div className={styles.logoIcon}></div>
            <h1 className={styles.title}>CybPass Athena</h1>
          </div>
          <p className={styles.subtitle}>Account Activation</p>
        </div>

        <div className={styles.divider}></div>

        {/* Success State */}
        {activationStatus === 'success' && (
          <div className={styles.content}>
            <div className={styles.iconSuccess}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className={styles.statusTitle}>Account Activated Successfully</h2>
            <p className={styles.statusDescription}>
              Welcome, {activationResult?.email}. Your account has been activated and is ready to use.
            </p>
            <div className={styles.infoBox}>
              <h3 className={styles.infoTitle}>Next Steps</h3>
              <ol className={styles.stepsList}>
                <li className={styles.step}>Click "Continue to Login" below</li>
                <li className={styles.step}>Authenticate using Google or Microsoft OAuth</li>
                <li className={styles.step}>Access CybPass Athena and begin your analysis</li>
              </ol>
            </div>
            <button className={styles.primaryButton} onClick={handleGoToLogin}>
              Continue to Login
            </button>
          </div>
        )}

        {/* Already Used State */}
        {activationStatus === 'already_used' && (
          <div className={styles.content}>
            <div className={styles.iconWarning}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className={styles.statusTitle}>Activation Link Already Used</h2>
            <p className={styles.statusDescription}>
              This activation link has already been used to activate an account.
            </p>
            <div className={styles.warningBox}>
              <p className={styles.warningText}>
                If you activated this account, proceed to login. If you did not activate this account, please contact your workspace administrator immediately.
              </p>
            </div>
            <button className={styles.primaryButton} onClick={handleGoToLogin}>
              Continue to Login
            </button>
          </div>
        )}

        {/* Expired State */}
        {activationStatus === 'expired' && (
          <div className={styles.content}>
            <div className={styles.iconError}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className={styles.statusTitle}>Activation Link Expired</h2>
            <p className={styles.statusDescription}>
              This activation link has expired. Activation links are valid for 72 hours.
            </p>
            <div className={styles.errorBox}>
              <p className={styles.errorText}>
                Please contact your workspace administrator to request a new invitation.
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {activationStatus === 'error' && (
          <div className={styles.content}>
            <div className={styles.iconError}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className={styles.statusTitle}>Activation Failed</h2>
            <div className={styles.errorBox}>
              <p className={styles.errorText}>{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Pending Activation State */}
        {!activationStatus && tokenInfo && (
          <div className={styles.content}>
            <div className={styles.userCard}>
              <Avatar size="lg">
                <AvatarFallback>
                  <Icon name="user" size={24} />
                </AvatarFallback>
              </Avatar>
              <div className={styles.userInfo}>
                <p className={styles.userLabel}>Activating Account For</p>
                <h3 className={styles.userName}>{tokenInfo.name}</h3>
                <p className={styles.userEmail}>{tokenInfo.email}</p>
              </div>
            </div>

            <div className={styles.metadataBox}>
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>Invited By</span>
                <span className={styles.metadataValue}>{tokenInfo.invited_by}</span>
              </div>
            </div>

            <div className={styles.instructionBox}>
              <p className={styles.instructionText}>
                Click the button below to activate your CybPass Athena account and gain access to the platform.
              </p>
            </div>

            <button
              className={styles.primaryButton}
              onClick={handleActivate}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className={styles.buttonSpinner}></span>
                  <span>Activating Account...</span>
                </>
              ) : (
                'Activate Account'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <p className={styles.footerText}>
          © 2025 CybPass. All rights reserved. | Secure Automotive Cybersecurity Platform
        </p>
      </div>
    </div>
  );
};

export default ActivateAccount;
